import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
import axios from '../../hooks/fetchWithAuth';
import config from '../../config';
import getCookie from '../../hooks/getCookie';
import './style.css';

// Custom Mention Blot
const Inline = Quill.import('blots/inline');

class MentionBlot extends Inline {
    static blotName = 'mention';
    static tagName = 'span';
    static className = 'ql-mention';

    static create(value) {
        const node = super.create();
        node.setAttribute('data-id', value.id);
        node.setAttribute('data-value', value.value);
        node.setAttribute('data-denotation-char', value.denotationChar || '@');
        node.textContent = `${value.denotationChar || '@'}${value.value}`;
        node.setAttribute('contenteditable', 'false');
        return node;
    }

    static formats(node) {
        return {
            id: node.getAttribute('data-id'),
            value: node.getAttribute('data-value'),
            denotationChar: node.getAttribute('data-denotation-char')
        };
    }

    format(name, value) {
        if (name !== this.statics.blotName || !value) return super.format(name, value);
        this.domNode.setAttribute('data-id', value.id);
        this.domNode.setAttribute('data-value', value.value);
        this.domNode.setAttribute('data-denotation-char', value.denotationChar || '@');
    }
}

// Register the blot
Quill.register(MentionBlot);

const MentionTextArea = ({ value, onChange, onSave, onCancel, placeholder, selectedCard }) => {
    const [users, setUsers] = useState([]);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
    const quillRef = useRef(null);
    const backendIp = config.backend_ip;
    const sessionId = getCookie("session_id");
    const username = getCookie("username");
    const boardId = getCookie("boardId");
    
    useEffect(() => {
        fetchBoardUsers();
    }, []);

    const fetchBoardUsers = async () => {
        try {
            const response = await axios.get(`${backendIp}/api/board/ti/get_board_users_for_mentions`, {
                params: {
                    board_id: boardId,
                    session_id: sessionId,
                    username: username
                }
            });
            setUsers(response.data);
        } catch (error) {
            return null;
        }
    };

    // Função para extrair menções do conteúdo HTML
    const extractMentionsFromHTML = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        const mentionSpans = tempDiv.querySelectorAll('.ql-mention');
        const mentions = [];
        
        mentionSpans.forEach(span => {
            const userId = span.getAttribute('data-id');
            const username = span.getAttribute('data-value');
            if (userId && username) {
                mentions.push({
                    id: userId,
                    username: username
                });
            }
        });
        
        return mentions;
    };

    // Função para converter HTML com menções para texto simples
    const convertHTMLToText = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        // Substituir elementos de menção por texto simples @username
        const mentionSpans = tempDiv.querySelectorAll('.ql-mention');
        mentionSpans.forEach(span => {
            const username = span.getAttribute('data-value');
            span.replaceWith(`@${username}`);
        });
        
        return tempDiv.textContent || tempDiv.innerText || '';
    };

    const handleTextChange = (content, delta, source, editor) => {
        // Extrair menções do conteúdo HTML
        const mentions = extractMentionsFromHTML(content);
        
        // Converter para texto simples para compatibilidade
        const textContent = convertHTMLToText(content);
        
        // Chamar onChange com o conteúdo original e informações adicionais
        onChange(content, {
            mentions: mentions,
            textContent: textContent
        });
        
        // Verificar se o usuário digitou @
        const text = editor.getText();
        const selection = editor.getSelection();
        
        if (selection && text) {
            const beforeCursor = text.substring(0, selection.index);
            const lastAtIndex = beforeCursor.lastIndexOf('@');
            
            if (lastAtIndex !== -1) {
                const afterAt = beforeCursor.substring(lastAtIndex + 1);
                
                // Se não há espaço após @, mostrar sugestões
                if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
                    setMentionSearch(afterAt);
                    setShowMentions(true);
                    
                    // Calcular posição absoluta em relação ao viewport
                    const quillContainer = quillRef.current.getEditor().container;
                    const rect = quillContainer.getBoundingClientRect();
                    
                    const newPosition = {
                        top: rect.bottom + window.scrollY + 5,
                        left: rect.left + window.scrollX + 10
                    };                  
                    
                    setMentionPosition(newPosition);
                } else {
                    setShowMentions(false);
                }
            } else {
                setShowMentions(false);
            }
        }
    };

    const insertMention = (user) => {
        const quill = quillRef.current.getEditor();
        const selection = quill.getSelection();

        if (!selection) return;

        const text = quill.getText();
        const beforeCursor = text.substring(0, selection.index);
        const lastAtIndex = beforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            // Delete the @ and typed text
            const deleteLength = selection.index - lastAtIndex;
            quill.deleteText(lastAtIndex, deleteLength);

            // Insert the mention as plain text and as a span for Quill formatting
            // This ensures both the HTML and the text are correct for notifications
            const insertIndex = lastAtIndex;
            // Insert the mention using custom blot
            quill.insertEmbed(insertIndex, 'mention', {
                id: user.user_id,
                value: user.username,
                denotationChar: '@'
            });
            // Insert a space after the mention
            quill.insertText(insertIndex + 1, ' ');

            // Move cursor to after the mention and space
            quill.setSelection(insertIndex + 2);

            // Force update of the content and call onChange with correct additionalData
            const newContent = quill.root.innerHTML;
            // Extract mentions and plain text for notification logic
            const mentions = extractMentionsFromHTML(newContent);
            const textContent = convertHTMLToText(newContent);

            onChange(newContent, {
                mentions: mentions,
                textContent: textContent
            });
        }

        setShowMentions(false);
        setMentionSearch('');
    };

    const filteredUsers = users.filter(user =>
        user.display_name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
        user.username.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, 
             {'indent': '-1'}, {'indent': '+1'}],
            ['image'],
        ],
    };

    // Renderizar o popup usando portal
    const renderMentionPopup = () => {
        if (!showMentions || filteredUsers.length === 0) return null;

        return ReactDOM.createPortal(
            <div 
                className="mention-popup"
                style={{
                    position: 'fixed',
                    top: `${mentionPosition.top}px`,
                    left: `${mentionPosition.left}px`,
                    zIndex: 99999
                }}
            >
                {filteredUsers.slice(0, 10).map((user) => (
                    <div
                        key={user.user_id}
                        className="mention-item"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            insertMention(user);
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault(); // Previne perda de foco do editor
                        }}
                    >
                        <strong>@{user.username}</strong>
                        <span>{user.display_name}</span>
                    </div>
                ))}
            </div>,
            document.body
        );
    };

    return (
        <div className="mention-textarea-container">
            <ReactQuill
                ref={quillRef}
                value={value}
                onChange={handleTextChange}
                modules={modules}
                placeholder={placeholder}
                style={{ height: 'auto', color: '#ffffff' }}
                className='textfield-comments'
            />
            
            {renderMentionPopup()}
        </div>
    );
};

export default MentionTextArea;